"use client";

import { authenticatedFetch } from "@/lib/auth";
import type { UserSubscriptionState } from "@/schemas/subscription";
import { useRouter } from "next/navigation";
import { type ReactNode, createContext, useContext, useState } from "react";

const UserSubscriptionContext = createContext<
  | (UserSubscriptionState & {
      refreshSubscription: () => Promise<void>;
    })
  | undefined
>(undefined);

interface UserSubscriptionProviderProps {
  children: ReactNode;
  initialState: UserSubscriptionState;
}

export function UserSubscriptionProvider({
  children,
  initialState,
}: UserSubscriptionProviderProps) {
  const [state, setState] = useState<UserSubscriptionState>(initialState);
  const router = useRouter();

  const refreshSubscription = async () => {
    try {
      const response = await authenticatedFetch("/api/subscription-data");
      if (!response.ok) {
        throw new Error("Failed to refresh subscription");
      }
      const data = await response.json();
      setState(data);

      // If we got a redirect URL, use it
      if (data.redirectUrl) {
        router.replace(data.redirectUrl);
      }
    } catch (error) {
      // Error is handled silently as this is a background refresh
    }
  };

  return (
    <UserSubscriptionContext.Provider value={{ ...state, refreshSubscription }}>
      {children}
    </UserSubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(UserSubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a UserSubscriptionProvider",
    );
  }
  return context;
}

// Backward compatibility hooks
export function useProUser() {
  const context = useSubscription();
  return {
    ...context,
    isGrowth: context.tier === "growth",
    isStarter: context.tier === "starter",
    isPaid: context.tier !== "free",
  };
}

// Keep old name for backward compatibility
export { UserSubscriptionProvider as ProUserProvider };
